// ABOUTME: The 1A breathing stage: the Too Much slices slot while idle, the ring sweep while a round is armed, and a round live region.
// ABOUTME: Presentation only; the screen owns the round state and passes the phase, the length and the seconds left.

import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CROSSING_RING_TRACK } from '../../../../theme/crossingTokens';
import { breathPct } from '../../crossing/breathMath';
import { useBreathStageScale } from '../../crossing/useBreathStageScale';
import { visuallyHidden } from '../../runner/visuallyHidden';
import type { RoundPhase } from './useBreathingRound';

// Spoken by the live region when a breathing round starts and when it finishes.
const ROUND_STARTED = 'Breathing round started.';
const ROUND_COMPLETE = 'Breathing round complete.';

const COMPOSITE_SIZE = 460;
// The round's ring sweep: the reference draws it 470 px wide, scaled 1.28 on desktop and 0.88 on a phone, then by the frame scale.
const RING_BASE = 470;

interface BreathingStageProps {
  phase: RoundPhase;
  total: number; // seconds in the current round
  leftSec: number; // whole seconds left
  petals: ReactNode; // the Too Much slices, centred on the canoe
}

export default function BreathingStage({ phase, total, leftSec, petals }: BreathingStageProps) {
  const armed = phase === 'running' || phase === 'done';
  // The scene's stage rides the same scale and the same raise, so the canoe is always at the ring's center.
  const { ringScale, raisePx } = useBreathStageScale();
  const ringDiameter = RING_BASE * ringScale;

  // Always mounted, so a screen reader hears the text change rather than a new region appear.
  const announcement = phase === 'running' ? ROUND_STARTED : phase === 'done' ? ROUND_COMPLETE : '';

  return (
    <Box sx={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 3 }}>
      <Box sx={{ position: 'relative', width: COMPOSITE_SIZE, height: COMPOSITE_SIZE }}>
        {petals && <Box data-testid="too-much-seam" style={{ transform: `translateY(${-raisePx}px)` }} sx={{ position: 'absolute', inset: 0 }}>{petals}</Box>}

        {/* The round's ring sweep (breathPct), the reference's conic-gradient masked to a thin ring around the scene's canoe.
            data-sweep exposes the raw value for testing: happy-dom's CSS engine does not parse conic-gradient(). */}
        {armed && (
          <Box
            data-testid="breath-ring-frame"
            data-diameter={ringDiameter}
            sx={{ position: 'absolute', left: '50%', pointerEvents: 'none' }}
            style={{ width: ringDiameter, height: ringDiameter, top: `calc(50% - ${raisePx}px)`, transform: 'translate(-50%, -50%)' }}
          >
            <Box
              data-testid="breath-ring"
              data-sweep={breathPct(total, leftSec)}
              sx={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                background: `conic-gradient(${alpha(CROSSING_RING_TRACK, 0.85)} ${breathPct(total, leftSec)}, ${alpha(CROSSING_RING_TRACK, 0)} 0)`,
                WebkitMask: 'radial-gradient(closest-side, transparent calc(100% - 9px), black calc(100% - 8px))',
                mask: 'radial-gradient(closest-side, transparent calc(100% - 9px), black calc(100% - 8px))',
              }}
            />
          </Box>
        )}
      </Box>

      <Box role="status" aria-live="polite" aria-atomic="true" sx={visuallyHidden}>{announcement}</Box>
    </Box>
  );
}
